import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const REQUEST_TIMEOUT_MS = 15000;

// Public API instance - no credentials needed for public endpoints
const publicApi = axios.create({
  baseURL: API_BASE,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
});

// Authenticated API instance - with credentials for admin endpoints
const authApi = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
});

// Add response interceptor for error handling
const handleError = (error) => {
  return Promise.reject(error);
};

publicApi.interceptors.response.use((response) => response, handleError);
authApi.interceptors.response.use((response) => response, handleError);

export const api = {
  // Health check
  healthCheck: async () => {
    const response = await publicApi.get("/health");
    return response.data;
  },

  // Seed data
  seedData: async () => {
    const response = await publicApi.post("/seed");
    return response.data;
  },

  // Events - Public
  getEvents: async () => {
    const response = await publicApi.get("/events");
    return response.data;
  },

  getEventBySlug: async (slug) => {
    const response = await publicApi.get(`/events/${slug}`);
    return response.data;
  },

  // Photos - Public
  getPhotos: async () => {
    const response = await publicApi.get("/photos");
    return response.data;
  },

  getPhoto: async (id) => {
    const response = await publicApi.get(`/photos/${id}`);
    return response.data;
  },

  // Content - Public
  getAllContent: async () => {
    const response = await publicApi.get("/content");
    return response.data;
  },

  getContent: async (key) => {
    const response = await publicApi.get(`/content/${key}`);
    return response.data;
  },

  // YouTube Config - Public
  getYouTubeConfig: async () => {
    const response = await publicApi.get("/youtube/config");
    return response.data;
  },

  // YouTube Latest Video - Public
  getLatestYouTubeVideo: async () => {
    const response = await publicApi.get("/youtube/latest");
    return response.data;
  },

  // Auth - Uses credentials
  login: async (email, password) => {
    const response = await authApi.post("/auth/login", { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await authApi.post("/auth/logout");
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await authApi.get("/auth/me");
    return response.data;
  },

  // Events - Admin (requires auth)
  createEvent: async (eventData) => {
    const response = await authApi.post("/admin/events", eventData);
    return response.data;
  },

  updateEvent: async (id, eventData) => {
    const response = await authApi.put(`/admin/events/${id}`, eventData);
    return response.data;
  },

  deleteEvent: async (id) => {
    const response = await authApi.delete(`/admin/events/${id}`);
    return response.data;
  },

  // Photos - Admin (requires auth)
  uploadPhoto: async (formData) => {
    const response = await authApi.post("/admin/photos", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  deletePhoto: async (id) => {
    const response = await authApi.delete(`/admin/photos/${id}`);
    return response.data;
  },

  // Content - Admin (requires auth)
  updateContent: async (key, value) => {
    const response = await authApi.put(`/admin/content/${key}`, { value });
    return response.data;
  },

  // YouTube - Admin (requires auth)
  updateYouTubeConfig: async (config) => {
    const response = await authApi.put("/admin/youtube/config", config);
    return response.data;
  },

  // Shop Items - Public
  getShopItems: async () => {
    const response = await publicApi.get("/shop/items");
    return response.data;
  },

  // Shop Items - Admin (returns ALL items including hidden ones)
  getAdminShopItems: async () => {
    const response = await authApi.get("/admin/shop/items");
    return response.data;
  },

  // Shop Items - Admin (requires auth)
  createShopItem: async (itemData) => {
    const response = await authApi.post("/admin/shop/items", itemData);
    return response.data;
  },

  updateShopItem: async (id, itemData) => {
    const response = await authApi.put(`/admin/shop/items/${id}`, itemData);
    return response.data;
  },

  deleteShopItem: async (id) => {
    const response = await authApi.delete(`/admin/shop/items/${id}`);
    return response.data;
  },

  reorderShopItems: async (ids) => {
    const response = await authApi.post("/admin/shop/items/reorder", { ids });
    return response.data;
  },

  // Stripe Checkout - Public
  createCheckoutSession: async (cartItems, originUrl) => {
    const response = await publicApi.post("/shop/checkout", {
      items: cartItems.map((li) => ({
        item_id: li.item_id,
        variation_id: li.variation_id || null,
        quantity: li.quantity,
      })),
      origin_url: originUrl,
    });
    return response.data; // { url, session_id, order_id }
  },

  getCheckoutStatus: async (sessionId) => {
    const response = await publicApi.get(`/shop/checkout/status/${sessionId}`);
    return response.data;
  },

  // Public: fetch a single product with its variations + images by slug
  getShopProduct: async (slug) => {
    const response = await publicApi.get(`/shop/product/${slug}`);
    return response.data;
  },

  // Admin: variation CRUD
  addVariation: async (itemId, payload) => {
    const response = await authApi.post(`/admin/shop/items/${itemId}/variations`, payload);
    return response.data;
  },
  updateVariation: async (itemId, variationId, payload) => {
    const response = await authApi.put(
      `/admin/shop/items/${itemId}/variations/${variationId}`,
      payload
    );
    return response.data;
  },
  deleteVariation: async (itemId, variationId) => {
    const response = await authApi.delete(
      `/admin/shop/items/${itemId}/variations/${variationId}`
    );
    return response.data;
  },
  reorderVariations: async (itemId, ids) => {
    const response = await authApi.post(
      `/admin/shop/items/${itemId}/variations/reorder`,
      { ids }
    );
    return response.data;
  },

  // Admin: upload a single image, return its URL (shared with photos endpoint)
  uploadAdminImage: async (file) => {
    const form = new FormData();
    form.append("file", file);
    const response = await authApi.post("/admin/upload-image", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data; // { url, storage_path }
  },

  // Orders - Admin (requires auth)
  getOrders: async ({ status, limit = 100 } = {}) => {
    const params = new URLSearchParams({ limit });
    if (status) params.append("status", status);
    const response = await authApi.get(`/admin/orders?${params}`);
    return response.data; // { orders: [], count }
  },

  refreshStaleOrders: async () => {
    const response = await authApi.post("/admin/orders/refresh-stale");
    return response.data; // { checked, paid, expired, still_pending, errors }
  },

  deleteOrder: async (orderId) => {
    const response = await authApi.delete(`/admin/orders/${orderId}`);
    return response.data;
  },

  // Backup / Restore - Admin (requires auth)
  downloadBackup: async () => {
    const response = await authApi.get("/admin/backup", { responseType: "blob" });
    return response; // caller handles the blob + headers
  },

  restoreBackup: async (backupJson) => {
    const response = await authApi.post("/admin/restore", backupJson);
    return response.data;
  },

  // Analytics - Admin (requires auth)
  getAnalyticsSummary: async (days = 7) => {
    const response = await authApi.get(`/admin/analytics/summary?days=${days}`);
    return response.data;
  },

  // Lightbox config - Public
  getLightboxConfig: async () => {
    const response = await publicApi.get("/lightbox/config");
    return response.data;
  },

  // Lightbox config - Admin
  updateLightboxConfig: async (config) => {
    const response = await authApi.put("/admin/lightbox/config", config);
    return response.data;
  },

  // Email subscribe - Public
  subscribe: async (email) => {
    const response = await publicApi.post("/subscribe", { email });
    return response.data;
  },

  // Subscribers - Admin
  getSubscribers: async () => {
    const response = await authApi.get("/admin/subscribers");
    return response.data;
  },

  deleteSubscriber: async (id) => {
    const response = await authApi.delete(`/admin/subscribers/${id}`);
    return response.data;
  },

  exportSubscribersCsv: async () => {
    const response = await authApi.get("/admin/subscribers/export.csv", { responseType: "blob" });
    return response;
  },

  // Generic admin image upload (for lightbox promo etc.) - returns {url, storage_path}
  uploadAdminImage: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await authApi.post("/admin/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  // Fan Questions - Public
  submitFanQuestion: async (data) => {
    const response = await publicApi.post("/fan-questions", data);
    return response.data;
  },

  // Fan Questions - Admin
  getFanQuestions: async ({ page = 1, pageSize = 25, q = "" } = {}) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (q) params.append("q", q);
    const response = await authApi.get(`/admin/fan-questions?${params}`);
    return response.data;
  },

  deleteFanQuestion: async (id) => {
    const response = await authApi.delete(`/admin/fan-questions/${id}`);
    return response.data;
  },

  exportFanQuestionsCsv: async () => {
    const response = await authApi.get("/admin/fan-questions/export.csv", { responseType: "blob" });
    return response;
  },
};

export default api;
